import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IconPlus, IconDeviceFloppy, IconTrash, IconUser, IconCalendarEvent, IconUsers, IconBriefcase, IconBuildingStore, IconCurrencyDollar, IconReceipt, IconReportMoney, IconBox, IconAirBalloon, IconAirBalloonFilled } from '@tabler/icons-react';
import {
    SETTINGS_USERS_PATH,
    RESERVATIONS_PATH,
    PERSONS_PATH,
    SERVICES_PATH,
    BUSINESSES_PATH,
    SERVICE_ORDERS_PATH,
    SERVICE_BOOKINGS_PATH,
    INVENTORY_ITEMS_PATH,
    SERVICE_BOOKING_PATH,
    SERVICE_PATH,
    SERVICE_GROUP_PATH
} from '@/lib/navigation';

interface ApiLogActionProps {
    method: string;
    url: string;
    body: any;
}

export function ApiLogAction({ method, url, body }: ApiLogActionProps) {
    const { t } = useTranslation();
    const data = body || {};

    const renderAction = (key: string, linkNode?: React.ReactNode, extraDetails?: React.ReactNode) => {
        // Determine action type
        const isCreate = key.includes('_created') || key.includes('_added');
        const isUpdate = key.includes('_updated');
        const isDelete = key.includes('_deleted') || key.includes('_removed');

        let ActionIcon = IconPlus;
        let colorClass = 'text-gray-600 bg-gray-100';

        if (isCreate) {
            ActionIcon = IconPlus;
            colorClass = 'text-green-600 bg-green-100';
        } else if (isUpdate) {
            ActionIcon = IconDeviceFloppy;
            colorClass = 'text-blue-600 bg-blue-100';
        } else if (isDelete) {
            ActionIcon = IconTrash;
            colorClass = 'text-red-600 bg-red-100';
        }

        // Entity icons (optional, based on key name)
        let EntityIcon = null;
        if (key.includes('user')) EntityIcon = IconUser;
        else if (key.includes('reservation')) EntityIcon = IconCalendarEvent;
        else if (key.includes('person')) EntityIcon = IconUsers;
        else if (key.includes('tenant_business') || key.includes('business') || key.includes('provider')) EntityIcon = IconBuildingStore;
        else if (key.includes('service_price')) EntityIcon = IconCurrencyDollar;
        else if (key.includes('service_cost')) EntityIcon = IconReportMoney;
        else if (key.includes('service_order')) EntityIcon = IconReceipt;
        else if (key.includes('service_booking')) EntityIcon = IconAirBalloonFilled;
        else if (key.includes('service_group')) EntityIcon = IconBox; // generic for groups
        else if (key.includes('service')) EntityIcon = IconAirBalloon;
        else if (key.includes('inventory')) EntityIcon = IconBox;

        // We want the full descriptive text from es.ts, so we just use the translation key directly
        const label = t(`api_logs.${key}`);

        return (
            <div className="flex flex-col">
                <div className="flex items-center gap-1.5">
                    {/* <div className={`p-1 rounded-md flex items-center justify-center ${colorClass}`}>
                        <ActionIcon size={14} stroke={2.5} />
                    </div> */}
                    {EntityIcon && <EntityIcon size={24} className={`${colorClass} p-1 rounded-md`} />}
                    <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                        {label}
                        {linkNode && <span>{linkNode}</span>}
                    </div>
                </div>
                {
                    extraDetails && (
                        <div className="text-xs text-gray-500 mt-1 pl-7">
                            {extraDetails}
                        </div>
                    )
                }
            </div>
        );
    };

    // Helpers
    const getId = (pathUrl: string, index: number) => pathUrl.split('/')[index];

    if (method === 'POST' && url === '/users') {
        return renderAction('new_user_created', null, data.email);
    }
    if (method === 'PUT' && url.match(/^\/users\/(\d+)$/)) {
        return renderAction('user_updated', null, data.email);
    }
    if (method === 'DELETE' && url.match(/^\/users\/(\d+)$/)) {
        return renderAction('user_deleted');
    }

    if (method === 'POST' && url === '/reservations') {
        return renderAction('reservation_created', null, data.name);
    }
    if (method === 'PUT' && url.match(/^\/reservations\/(\d+)$/)) {
        const id = getId(url, 2);
        return renderAction('reservation_updated', <Link to={`${RESERVATIONS_PATH}/${id}`}>#{id}</Link>, data.name);
    }
    if (method === 'DELETE' && url.match(/^\/reservations\/(\d+)$/)) {
        return renderAction('reservation_deleted', <span className="text-gray-500">#{getId(url, 2)}</span>);
    }

    if (method === 'POST' && url.match(/^\/reservations\/(\d+)\/persons\/(\d+)$/)) {
        const resId = getId(url, 2);
        const personId = getId(url, 4);
        return renderAction(
            'reservation_person_added',
            <Link to={`${RESERVATIONS_PATH}/${resId}`}>#{resId}</Link>,
            <div className="flex items-center gap-1 mt-0.5">
                <span className="text-gray-500">Persona</span>
                <Link to={`${PERSONS_PATH}/${personId}`} className="font-semibold text-blue-600 hover:underline">#{personId}</Link>
            </div>
        );
    }
    if (method === 'DELETE' && url.match(/^\/reservations\/(\d+)\/persons\/(\d+)$/)) {
        const resId = getId(url, 2);
        const personId = getId(url, 4);
        return renderAction(
            'reservation_person_removed',
            <Link to={`${RESERVATIONS_PATH}/${resId}`}>#{resId}</Link>,
            <div className="flex items-center gap-1 mt-0.5">
                <span className="text-gray-500">Persona</span>
                <Link to={`${PERSONS_PATH}/${personId}`} className="font-semibold text-blue-600 hover:underline">#{personId}</Link>
            </div>
        );
    }

    if (method === 'POST' && url === '/persons') {
        return renderAction('person_created', null, `${data.name || ''}`);
    }
    if (method === 'PUT' && url.match(/^\/persons\/(\d+)$/)) {
        const id = getId(url, 2);
        return renderAction('person_updated', <Link to={`${PERSONS_PATH}/${id}`}>#{id}</Link>, `${data.name || ''}`);
    }
    if (method === 'DELETE' && url.match(/^\/persons\/(\d+)$/)) {
        return renderAction('person_deleted', <span className="text-gray-500">#{getId(url, 2)}</span>);
    }

    if (method === 'POST' && url === '/businesses') {
        return renderAction('business_created', null, data.name);
    }
    if (method === 'PUT' && url.match(/^\/businesses\/(\d+)$/)) {
        const id = getId(url, 2);
        return renderAction('business_updated', <Link to={`${BUSINESSES_PATH}/${id}`}>#{id}</Link>, data.name);
    }
    if (method === 'DELETE' && url.match(/^\/businesses\/(\d+)$/)) {
        return renderAction('business_deleted', <span className="text-gray-500">#{getId(url, 2)}</span>);
    }

    if (method === 'POST' && url === '/services') {
        return renderAction('service_created', null, data.name);
    }
    if (method === 'PUT' && url.match(/^\/services\/(\d+)$/)) {
        const id = getId(url, 2);
        return renderAction('service_updated', <Link to={`${SERVICE_PATH.replace(':id', id)}`}>#{id}</Link>, data.name);
    }
    if (method === 'DELETE' && url.match(/^\/services\/(\d+)$/)) {
        return renderAction('service_deleted', <span className="text-gray-500">#{getId(url, 2)}</span>);
    }

    if (method === 'POST' && url === '/service-providers') {
        return renderAction('provider_created');
    }
    if (method === 'PUT' && url.match(/^\/service-providers\/(\d+)$/)) {
        const id = getId(url, 2);
        return renderAction('provider_updated', <Link to={`${BUSINESSES_PATH}/${id}`}>#{id}</Link>);
    }
    if (method === 'DELETE' && url.match(/^\/service-providers\/(\d+)$/)) {
        return renderAction('provider_deleted', <span className="text-gray-500">#{getId(url, 2)}</span>);
    }

    const priceTag = (amount: number, currency: string) => (
        <div className="inline-flex items-cente">
            {amount} {currency}
        </div>
    );

    if (method === 'POST' && url === '/service-sell-prices') {
        return renderAction('service_price_created', null, priceTag(data.default_amount, data.default_currency));
    }
    if (method === 'PUT' && url.match(/^\/service-sell-prices\/(\d+)$/)) {
        return renderAction('service_price_updated', null, priceTag(data.default_amount, data.default_currency));
    }
    if (method === 'DELETE' && url.match(/^\/service-sell-prices\/(\d+)$/)) {
        return renderAction('service_price_deleted');
    }

    if (method === 'POST' && url === '/service-sell-costs') {
        return renderAction('service_cost_created', null, priceTag(data.default_amount, data.default_currency));
    }
    if (method === 'PUT' && url.match(/^\/service-sell-costs\/(\d+)$/)) {
        return renderAction('service_cost_updated', null, priceTag(data.default_amount, data.default_currency));
    }
    if (method === 'DELETE' && url.match(/^\/service-sell-costs\/(\d+)$/)) {
        return renderAction('service_cost_deleted');
    }

    if (method === 'POST' && url === '/service-orders') {
        return renderAction('service_order_created', null, data.title);
    }
    if (method === 'PUT' && url.match(/^\/service-orders\/(\d+)$/)) {
        const id = getId(url, 2);
        return renderAction('service_order_updated', <Link to={`${SERVICE_ORDERS_PATH}/${id}`}>#{id}</Link>, data.title);
    }
    if (method === 'DELETE' && url.match(/^\/service-orders\/(\d+)$/)) {
        return renderAction('service_order_deleted', <span className="text-gray-500">#{getId(url, 2)}</span>);
    }

    if (method === 'POST' && url === '/service-bookings') {
        return renderAction('service_booking_created', null, data.title);
    }
    if (method === 'PUT' && url.match(/^\/service-bookings\/(\d+)$/)) {
        const id = getId(url, 2);
        return renderAction('service_booking_updated', <Link to={`${SERVICE_BOOKING_PATH.replace(':id', id)}`}>#{id}</Link>, data.title);
    }
    if (method === 'DELETE' && url.match(/^\/service-bookings\/(\d+)$/)) {
        return renderAction('service_booking_deleted', <span className="text-gray-500">#{getId(url, 2)}</span>);
    }

    if (method === 'POST' && url === '/inventory-items') {
        return renderAction('inventory_item_created', null, data.name);
    }
    if (method === 'PUT' && url.match(/^\/inventory-items\/(\d+)$/)) {
        const id = getId(url, 2);
        return renderAction('inventory_item_updated', <Link to={`${INVENTORY_ITEMS_PATH}`}>#{id}</Link>, data.name);
    }
    if (method === 'DELETE' && url.match(/^\/inventory-items\/(\d+)$/)) {
        return renderAction('inventory_item_deleted', <span className="text-gray-500">#{getId(url, 2)}</span>);
    }

    if (method === 'POST' && url === '/tenant-businesses') {
        return renderAction('tenant_business_created', null, data.name);
    }
    if (method === 'PUT' && url.match(/^\/tenant-businesses\/(\d+)$/)) {
        const id = getId(url, 2);
        return renderAction('tenant_business_updated', <span className="text-gray-500">#{id}</span>, data.name);
    }
    if (method === 'DELETE' && url.match(/^\/tenant-businesses\/(\d+)$/)) {
        return renderAction('tenant_business_deleted', <span className="text-gray-500">#{getId(url, 2)}</span>);
    }

    if (method === 'POST' && url === '/service-groups') {
        return renderAction('service_group_created', null, data.name);
    }
    if (method === 'PUT' && url.match(/^\/service-groups\/(\d+)$/)) {
        const id = getId(url, 2);
        return renderAction('service_group_updated', <Link to={`${SERVICE_GROUP_PATH.replace(':id', id)}`}>#{id}</Link>, data.name);
    }
    if (method === 'DELETE' && url.match(/^\/service-groups\/(\d+)$/)) {
        return renderAction('service_group_deleted', <span className="text-gray-500">#{getId(url, 2)}</span>);
    }

    if (method === 'PUT' && url.match(/^\/service-groups-service-discount\/(\d+)$/)) {
        const id = getId(url, 2);
        return renderAction('service_group_discount_updated', <span className="text-gray-500">#{id}</span>);
    }

    // Generic fallback for unmapped routes
    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
                <div
                    className={`
                        text-xs rounded-md px-2 py-0.5
                        font-semibold
                        w-fit
                        ${method === 'POST' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}
                        ${method === 'PUT' ? 'bg-yellow-100 text-yellow-600' : 'bg-blue-100 text-blue-600'}
                        ${method === 'DELETE' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}
                        ${method === 'PATCH' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}
                        ${method === 'HEAD' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-600'}
                        ${method === 'OPTIONS' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-600'}
                        ${method === 'CONNECT' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-600'}
                        ${method === 'TRACE' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-600'}
                    `}
                >
                    {method}
                </div>
                <span className=" text-xs text-gray-700 font-semibold">{url}</span>
            </div>
        </div>
    );
}

export function ImportantDataDisplay({ body }: { body: any }) {
    if (!body || typeof body !== 'object') {
        return "";
    }

    const omitKeys = [
        'id', 'tenant_id', 'created_at', 'updated_at', 'created_by', 'updated_by', 'deleted_at',
        'last_sell_price_update', 'last_sell_cost_update', 'password', 'token'
    ];

    const filtered = Object.keys(body).reduce((acc, key) => {
        if (!omitKeys.includes(key) && body[key] !== null && body[key] !== '') {
            acc[key] = body[key];
        }
        return acc;
    }, {} as Record<string, any>);

    if (Object.keys(filtered).length === 0) {
        return <span className="text-gray-400 italic text-xs">No extra data</span>;
    }

    // Function to map an ID key to a path if it exists
    const getLinkForIdKey = (key: string, value: any) => {
        if (key === 'service_id') return `${SERVICES_PATH.replace(':id', value)}`;
        if (key === 'business_id') return `${BUSINESSES_PATH}/${value}`;
        if (key === 'reservation_id') return `${RESERVATIONS_PATH}/${value}`;
        if (key === 'person_id') return `${PERSONS_PATH}/${value}`;
        if (key === 'user_id') return `${SETTINGS_USERS_PATH}/${value}`;
        if (key === 'service_order_id') return `${SERVICE_ORDERS_PATH}/${value}`;
        if (key === 'service_booking_id') return `${SERVICE_BOOKINGS_PATH}`; // bookings list doesnt support direct id well
        return null;
    };

    return (
        <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
            {Object.entries(filtered).map(([key, value]) => {
                let displayValue: React.ReactNode = String(value);

                if (typeof value === 'object') {
                    displayValue = JSON.stringify(value);
                } else if (typeof value === 'boolean') {
                    displayValue = value ? 'Yes' : 'No';
                } else if (key.endsWith('_id')) {
                    const linkPath = getLinkForIdKey(key, value);
                    if (linkPath) {
                        const labelName = key.replace('_id', '').replace('_', ' ');
                        const capitalized = labelName.charAt(0).toUpperCase() + labelName.slice(1);
                        displayValue = (
                            <Link to={linkPath} className="text-blue-600 hover:underline">
                                {capitalized} #{value}
                            </Link>
                        );
                    }
                }

                return (
                    <div key={key} className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-lg text-xs">
                        <span className="font-medium text-gray-400">{key}:</span>
                        <span className="text-gray-800 break-words line-clamp-1" title={typeof value !== 'object' ? String(value) : undefined}>
                            {displayValue}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
